import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

const DISABLED_PATH_PREFIXES = ['/meeting', '/extension-signed-in'];

const isServer = typeof window === 'undefined';
const isDev = import.meta.env.DEV;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let posthogInstance: any = null;

function getPostHog() {
  return posthogInstance;
}

async function initPostHog() {
  if (isServer || isDev || posthogInstance || !POSTHOG_KEY) return;

  const posthogModule = await import('posthog-js');
  const posthog = posthogModule.default;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    capture_pageleave: true,
    defaults: '2026-01-30',
    disable_session_recording: true,
  });

  posthogInstance = posthog;
}

function isDisabledPath(pathname: string) {
  return DISABLED_PATH_PREFIXES.some((p) => pathname.startsWith(p));
}

function PostHogPageTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const ph = getPostHog();
    if (!ph) return;

    if (isDisabledPath(pathname)) {
      ph.stopSessionRecording();
      return;
    }

    ph.capture('$pageview', { $current_url: window.location.href });

    if (!ph.sessionRecordingStarted()) {
      ph.startSessionRecording();
    }
  }, [pathname]);

  return null;
}

export function PostHogProviderWrapper({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(Boolean(getPostHog()));

  useEffect(() => {
    void initPostHog().then(() => {
      setIsReady(true);
    });
  }, []);

  if (isServer || !POSTHOG_KEY) return <>{children}</>;

  return (
    <>
      {isReady ? <PostHogPageTracker /> : null}
      {children}
    </>
  );
}

export { getPostHog };
